import { logger } from './logger';
/**
 * Parse attributes from XML-like tag opening
 * Extracts key="value" pairs from tag content
 */
const parseAttributes = (text) => {
    const attributeRegex = /(\w+)="([^"]*)"/g;
    const attributes = {};
    let match = attributeRegex.exec(text);
    while (match !== null) {
        attributes[match[1]] = match[2];
        match = attributeRegex.exec(text);
    }
    return attributes;
};
/**
 * Validate and sanitize email tag content
 * Note: This regex parser is designed for controlled AI-generated content.
 * For production with untrusted input, consider using a proper XML parser.
 */
export const parseEmailTags = (message) => {
    // Validate input
    if (!message || typeof message !== 'string') {
        return [{ type: 'text', content: '' }];
    }
    // Prevent potential ReDoS with length limit
    const MAX_MESSAGE_LENGTH = 100000; // 100KB limit
    if (message.length > MAX_MESSAGE_LENGTH) {
        console.warn('Message exceeds maximum length, truncating for parsing');
        message = message.substring(0, MAX_MESSAGE_LENGTH);
    }
    // Combined regex to match all email types in order
    const combinedRegex = /(<airiaNewEmail\s[^>]*?>(.*?)<\/airiaNewEmail>)|(<airiaReplyEmail>(.*?)<\/airiaReplyEmail>)|(<airiaReplaceEmail(\s[^>]*?)?>(.*?)<\/airiaReplaceEmail>)/gs;
    const parts = [];
    let lastIndex = 0;
    let matchCount = 0;
    const MAX_MATCHES = 50; // Prevent infinite loops
    let match = combinedRegex.exec(message);
    while (match !== null && matchCount < MAX_MATCHES) {
        matchCount++;
        // Add text content before the current tag
        if (match.index > lastIndex) {
            const textContent = message.substring(lastIndex, match.index).trim();
            if (textContent) {
                parts.push({
                    type: 'text',
                    content: textContent,
                });
            }
        }
        if (match[1]) {
            // This is a new email tag - match[1] is full tag, match[2] is body
            const fullMatch = match[1];
            const body = match[2];
            // Parse attributes from the opening tag
            const attributes = parseAttributes(fullMatch);
            // Validate required fields for new email
            if (attributes.to && attributes.subject && body) {
                parts.push({
                    type: 'email',
                    content: fullMatch,
                    emailData: {
                        to: attributes.to.trim(),
                        subject: attributes.subject.trim(),
                        body: body.trim(),
                        cc: attributes.cc?.trim(),
                    },
                });
            }
            else {
                logger.warn('Invalid airiaNewEmail tag: missing required attributes', attributes);
            }
        }
        else if (match[3]) {
            // This is a reply email tag - match[3] is full tag, match[4] is body
            const fullMatch = match[3];
            const body = match[4];
            if (body && body.trim()) {
                parts.push({
                    type: 'reply',
                    content: fullMatch,
                    replyEmailData: {
                        body: body.trim(),
                    },
                });
            }
            else {
                logger.warn('Invalid airiaReplyEmail tag: missing body content');
            }
        }
        else if (match[5]) {
            // This is a replace email tag - match[5] is full tag, match[7] is body
            const fullMatch = match[5];
            const body = match[7];
            // Parse attributes from the opening tag (optional subject)
            const attributes = parseAttributes(fullMatch);
            if (body && body.trim()) {
                const replaceData = {
                    body: body.trim(),
                };
                // Add optional subject if provided
                if (attributes.subject) {
                    replaceData.subject = attributes.subject.trim();
                    logger.log('Parsed airiaReplaceEmail with subject:', attributes.subject);
                }
                else {
                    logger.log('Parsed airiaReplaceEmail without subject. Attributes found:', JSON.stringify(Object.keys(attributes)));
                }
                parts.push({
                    type: 'replace',
                    content: fullMatch,
                    replaceEmailData: replaceData,
                });
            }
            else {
                logger.warn('Invalid airiaReplaceEmail tag: missing body content');
            }
        }
        lastIndex = match.index + match[0].length;
        match = combinedRegex.exec(message);
    }
    // Add remaining text content after the last tag
    if (lastIndex < message.length) {
        const textContent = message.substring(lastIndex).trim();
        if (textContent) {
            parts.push({
                type: 'text',
                content: textContent,
            });
        }
    }
    // If no email tags were found, return the entire message as text
    if (parts.length === 0) {
        parts.push({
            type: 'text',
            content: message,
        });
    }
    return parts;
};
